import apiClient from '../client';

import { API_ENDPOINTS } from '../endpoints';

import type {

  User,

  ApiResponse,

  Tender,

  TenderFilters,

  Offer,

  OfferFilters,

  Project,

  ProjectFilters,

  DashboardStats,

} from '../types';

import type {

  SubmitOfferRequest,

  UpdateOfferRequest,

  CreateProjectRequest,

  UpdateProjectRequest,

} from '../types';



export const clientService = {

  // Profile

  getProfile: async (): Promise<ApiResponse<User>> => {

    try {

      const response = await apiClient.get<ApiResponse<User>>(

        API_ENDPOINTS.CLIENT.PROFILE

      );

      return response.data;

    } catch (error: any) {

      // If 404, try to get user from /me endpoint

      if (error?.response?.status === 404) {

        const meResponse = await apiClient.get<ApiResponse<User>>(

          API_ENDPOINTS.AUTH.ME

        );

        return meResponse.data;

      }

      throw error;

    }

  },



  updateProfile: async (data: Record<string, any>): Promise<ApiResponse<User>> => {
    // Always use FormData and POST for better Laravel compatibility
    const formData = new FormData();

    // Append regular fields
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof File) {
        formData.append(key, value);
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          formData.append(`${key}[${index}]`, item.toString());
        });
      } else if (value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    // Add _method for Laravel to handle PUT via POST
    formData.append('_method', 'PUT');

    const response = await apiClient.post<ApiResponse<User>>(
      API_ENDPOINTS.CLIENT.PROFILE,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  updateProfileWithImage: async (
    data: Partial<User>,
    image?: File
  ): Promise<ApiResponse<User>> => {
    const formData = new FormData();
    
    // Add all text fields
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(item => formData.append(`${key}[]`, item.toString()));
        } else {
          formData.append(key, value.toString());
        }
      }
    });
    
    // Add image if provided
    if (image) {
      formData.append('profile_image', image);
    }
    
    const response = await apiClient.put<ApiResponse<User>>(
      API_ENDPOINTS.CLIENT.PROFILE,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return response.data;
  },



  uploadDocuments: async (

    files: File[]

  ): Promise<ApiResponse<any>> => {

    const formData = new FormData();

    

    files.forEach((file) => {

      formData.append('documents[]', file);

    });

    

    const response = await apiClient.post<ApiResponse<any>>(

      API_ENDPOINTS.CLIENT.PROFILE_DOCUMENTS,

      formData,

      {

        headers: {

          'Content-Type': 'multipart/form-data',

        },

      }

    );

    return response.data;

  },



  deleteDocument: async (

    documentId: number

  ): Promise<ApiResponse<any>> => {

    const response = await apiClient.delete<ApiResponse<any>>(

      `${API_ENDPOINTS.CLIENT.PROFILE_DOCUMENTS}/${documentId}`

    );

    return response.data;

  },



  // Tenders

  getTenders: async (

    filters?: TenderFilters

  ): Promise<ApiResponse<Tender[]>> => {

    try {

      // Default sort: newest first by created_at
      const paramsWithSort = {
        ...filters,
        sort_by: filters?.sort_by || 'created_at',
        sort_order: filters?.sort_order || 'desc'
      };

      const response = await apiClient.get<ApiResponse<Tender[]>>(

        API_ENDPOINTS.CLIENT.TENDERS,

        { params: paramsWithSort }

      );

      return response.data;

    } catch (error: any) {

      // If 500 or other errors, return empty list

      if (error?.response?.status >= 500) {

        return {

          success: false,

          data: [],

          message: 'Error loading tenders',

        };

      }

      throw error;

    }

  },



  getTenderDetails: async (id: number): Promise<ApiResponse<Tender>> => {

    const response = await apiClient.get<ApiResponse<Tender>>(

      API_ENDPOINTS.CLIENT.TENDER_DETAIL(id)

    );

    return response.data;

  },

  downloadTenderDocument: async (tenderId: number, documentId: number, fileName?: string): Promise<void> => {
    const response = await apiClient.get(
      API_ENDPOINTS.CLIENT.TENDER_DOCUMENT_DOWNLOAD(tenderId, documentId),
      {
        responseType: 'blob',
      }
    );

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },



  // Offers

  getOffers: async (

    filters?: OfferFilters

  ): Promise<ApiResponse<Offer[]>> => {

    // Default sort: newest first by submitted_at
    const paramsWithSort = {
      ...filters,
        sort_by: filters?.sort_by || 'submitted_at',
        sort_order: filters?.sort_order || 'desc'
    };

    const response = await apiClient.get<ApiResponse<Offer[]>>(

      API_ENDPOINTS.CLIENT.OFFERS,

      { params: paramsWithSort }

    );

    return response.data;

  },



  getOfferDetails: async (id: number): Promise<ApiResponse<Offer>> => {

    const response = await apiClient.get<ApiResponse<Offer>>(

      API_ENDPOINTS.CLIENT.OFFER_DETAIL(id)

    );

    return response.data;

  },



  submitOffer: async (

    data: SubmitOfferRequest

  ): Promise<ApiResponse<Offer>> => {

    const formData = new FormData();

    

    // Add basic fields

    formData.append('tender_id', data.tender_id.toString());

    formData.append('proposed_amount', data.proposed_amount.toString());

    

    if (data.notes) {

      formData.append('notes', data.notes);

    }

    

    // Add attachments if any

    if (data.attachments && data.attachments.length > 0) {

      data.attachments.forEach((file) => {

        formData.append('attachments[]', file);

      });

    }

    

    // Add document types if any

    if (data.document_types && data.document_types.length > 0) {

      data.document_types.forEach((type) => {

        formData.append('document_types[]', type);

      });

    }

    

    const response = await apiClient.post<ApiResponse<Offer>>(

      API_ENDPOINTS.CLIENT.OFFERS,

      formData,

      {

        headers: {

          'Content-Type': 'multipart/form-data',

        },

      }

    );

    return response.data;

  },



  updateOffer: async (

    id: number,

    data: UpdateOfferRequest

  ): Promise<ApiResponse<Offer>> => {

    const response = await apiClient.put<ApiResponse<Offer>>(

      API_ENDPOINTS.CLIENT.OFFER_DETAIL(id),

      data

    );

    return response.data;

  },



  deleteOffer: async (id: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.CLIENT.OFFER_DETAIL(id));

  },



  // Projects

  getProjects: async (

    filters?: ProjectFilters

  ): Promise<ApiResponse<Project[]>> => {

    try {

      // Default sort: newest first by created_at
      const paramsWithSort = {
        ...filters,
        sort_by: filters?.sort_by || 'created_at',
        sort_order: filters?.sort_order || 'desc'
      };

      const response = await apiClient.get<ApiResponse<Project[]>>(

        API_ENDPOINTS.CLIENT.PROJECTS, 

        { params: paramsWithSort }

      );

      return response.data;

    } catch (error: any) {

      // If 500 or other errors, return empty list

      if (error?.response?.status >= 500) {

        return {

          success: false,

          data: [],

          message: 'Error loading projects',

        };

      }

      throw error;

    }

  },



  getProjectDetails: async (id: number): Promise<ApiResponse<Project>> => {

    const response = await apiClient.get<ApiResponse<Project>>(

      API_ENDPOINTS.CLIENT.PROJECT_DETAIL(id)

    );

    return response.data;

  },



  createProject: async (
    data: CreateProjectRequest | FormData
  ): Promise<ApiResponse<Project>> => {
    const isFormData = data instanceof FormData;

    const response = await apiClient.post<ApiResponse<Project>>(
      API_ENDPOINTS.CLIENT.PROJECTS,
      data,
      isFormData ? {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      } : undefined
    );

    return response.data;
  },



  updateProject: async (
    id: number,
    data: UpdateProjectRequest | FormData
  ): Promise<ApiResponse<Project>> => {
    const isFormData = data instanceof FormData;

    // Use the dedicated POST route for file uploads
    if (isFormData) {
      const response = await apiClient.post<ApiResponse<Project>>(
        `${API_ENDPOINTS.CLIENT.PROJECT_DETAIL(id)}/update-with-files`,
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    }

    // Use regular PATCH for non-file updates
    const response = await apiClient.patch<ApiResponse<Project>>(
      API_ENDPOINTS.CLIENT.PROJECT_DETAIL(id),
      data
    );

    return response.data;
  },



  deleteProject: async (id: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.CLIENT.PROJECT_DETAIL(id));

  },



  // Dashboard

  getDashboard: async (): Promise<ApiResponse<DashboardStats>> => {

    try {

      const response = await apiClient.get<ApiResponse<DashboardStats>>(

        API_ENDPOINTS.CLIENT.DASHBOARD

      );

      return response.data;

    } catch (error: any) {

      // If 404, return empty dashboard stats

      if (error?.response?.status === 404) {

        return {

          success: false,

          data: {

            total_tenders: 0,

            active_tenders: 0,

            total_offers: 0,

            pending_offers: 0,

            accepted_offers: 0,

            recent_tenders: [],

            recent_offers: [],

          },

          message: 'Dashboard endpoint not found',

        };

      }

      throw error;

    }

  },

};

